import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { authService, UserProfile } from '../services/authService';
import axios from 'axios';
import { API_URL } from '../config/api';

// Функция для декодирования JWT токена
function parseJwt(token: string): any | null {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Ошибка парсинга JWT токена:', error);
    return null;
  }
}

// Функция для проверки истечения токена
function isTokenExpired(token: string): boolean {
  const decoded = parseJwt(token);
  if (!decoded || !decoded.exp) {
    return true;
  }
  
  const currentTime = Date.now() / 1000;
  return decoded.exp < currentTime;
}

// Функция для получения времени до истечения токена в минутах
function getTokenExpiryTime(token: string): number | null {
  const decoded = parseJwt(token);
  if (!decoded || !decoded.exp) {
    return null;
  }
  
  const currentTime = Date.now() / 1000;
  const timeUntilExpiry = decoded.exp - currentTime;
  return Math.floor(timeUntilExpiry / 60); // возвращаем в минутах
}

interface SubscriptionInfo {
  tariffName: string;
  type: 'Individual' | 'Team';
  daysRemaining: number;
  status: string;
  isActive: boolean;
  endDate?: string;
  tariffPlanId?: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  token: string | null;
  profile: UserProfile | null;
  subscription: SubscriptionInfo | null;
  hasActiveSubscription: boolean;
  login: (token: string) => void;
  logout: () => void;
  checkSubscription: () => Promise<void>;
  updateProfile: (updates: Partial<UserProfile>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [token, setToken] = useState<string | null>(() => {
    return localStorage.getItem('auth_token');
  });
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const login = useCallback((newToken: string) => {
    localStorage.setItem('auth_token', newToken);
    setToken(newToken);
  }, []);

  const clearUserCaches = useCallback(() => {
    try {
      // Удаляем кэши, которые не должны переживать смену пользователя
      const prefixesToClear = [
        'tasks_cache',      // старый ключ
        'tasks_cache_v2',   // новый user-scoped ключ
        'knowledge_cache'   // кэш базы знаний (разные версии)
      ];

      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (!k) continue;
        if (prefixesToClear.some(prefix => k.startsWith(prefix))) {
          keysToRemove.push(k);
        }
      }

      keysToRemove.forEach(k => localStorage.removeItem(k));
    } catch (e) {
      console.warn('[AuthContext] Не удалось очистить пользовательские кэши:', e);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      setToken(null);
      setProfile(null);
      setSubscription(null);
      localStorage.removeItem('auth_token');
      clearUserCaches();
      
      const logoutEvent = new CustomEvent('app-logout', {
        detail: {
          timestamp: Date.now()
        }
      });
      document.dispatchEvent(logoutEvent);
      console.log('Отправлено событие при выходе');
    }
  }, [clearUserCaches]);

  const checkSubscription = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await axios.get(`${API_URL}/usersubscription/current`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (response.data) {
        setSubscription({
          tariffName: response.data.name,
          type: response.data.type,
          daysRemaining: response.data.daysRemaining,
          status: response.data.status,
          isActive: response.data.daysRemaining > 0,
          endDate: response.data.endDate,
          tariffPlanId: response.data.tariffPlanId
        });
      }
    } catch (error) {
      console.error('Ошибка при проверке подписки:', error);
    }
  }, [token]);

  const updateProfile = useCallback((updates: Partial<UserProfile>) => {
    setProfile(prevProfile => {
      if (prevProfile) {
        const updatedProfile = { ...prevProfile, ...updates };
        
        const authData = localStorage.getItem('auth_data');
        if (authData) {
          try {
            const parsedAuthData = JSON.parse(authData);
            if (parsedAuthData.profile) {
              parsedAuthData.profile = updatedProfile;
              localStorage.setItem('auth_data', JSON.stringify(parsedAuthData));
            }
          } catch (e) {
            console.error('Error updating localStorage:', e);
          }
        }
        return updatedProfile;
      }
      return prevProfile;
    });
  }, []);

  const loadProfile = useCallback(async () => {
    if (!token) {
      setIsLoading(false);
      return;
    }
    
    try {
      const data = await authService.getProfile();
      const safeProfile: UserProfile = {
        id: data.id || '',
        nickname: data.nickname || '',
        email: data.email || ''
      };
      setProfile(safeProfile);
    } catch (error) {
      console.error('[AuthContext] Ошибка при загрузке профиля:', error);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    const storedToken = localStorage.getItem('auth_token');
    if (storedToken) {
      // Проверяем, не истёк ли токен
      if (isTokenExpired(storedToken)) {
        console.warn('[AuthContext] Токен истёк, выполняем выход из системы');
        localStorage.removeItem('auth_token');
        clearUserCaches();
        // ВАЖНО: также очищаем состояние токена, иначе isAuthenticated останется true
        setToken(null);
        setProfile(null);
        setSubscription(null);
        setIsLoading(false);
      } else {
        setToken(storedToken);
        
        // Проверяем, сколько времени осталось до истечения токена
        const minutesUntilExpiry = getTokenExpiryTime(storedToken);
        if (minutesUntilExpiry !== null) {
          console.log(`[AuthContext] Токен действителен ещё ${minutesUntilExpiry} минут`);
          
          // Если осталось меньше 24 часов, показываем предупреждение
          if (minutesUntilExpiry < 1440) { // 24 часа = 1440 минут
            console.warn(`[AuthContext] Внимание: токен истечёт через ${Math.floor(minutesUntilExpiry / 60)} часов ${minutesUntilExpiry % 60} минут`);
          }
        }
      }
    } else {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) {
      loadProfile();
      checkSubscription();
    }
  }, [token, loadProfile, checkSubscription]);

  // При восстановлении соединения пробуем восстановить профиль и подписку
  useEffect(() => {
    const onOnline = () => {
      const currentToken = localStorage.getItem('auth_token');
      if (currentToken) {
        loadProfile();
        checkSubscription();
      }
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [checkSubscription, loadProfile]);

  // Слушаем событие принудительного выхода из системы при ошибке 401
  useEffect(() => {
    const handleForceLogout = (event: CustomEvent) => {
      console.log('[AuthContext] Принудительный выход из системы:', event.detail);
      
      // Выполняем выход без обращения к серверу, так как токен уже недействителен
      setToken(null);
      setProfile(null);
      setSubscription(null);
      localStorage.removeItem('auth_token');
      clearUserCaches();
      
      // Перенаправляем на страницу входа
      setTimeout(() => {
        window.location.href = '/auth/login';
      }, 100);
    };

    document.addEventListener('force-logout', handleForceLogout as EventListener);
    
    return () => {
      document.removeEventListener('force-logout', handleForceLogout as EventListener);
    };
  }, []);

  const hasActiveSubscription = subscription?.isActive === true;

  const contextValue: AuthContextType = useMemo(() => ({
    isAuthenticated: !!token,
    isLoading,
    token,
    profile,
    subscription,
    hasActiveSubscription,
    login,
    logout,
    checkSubscription,
    updateProfile
  }), [token, isLoading, profile, subscription, hasActiveSubscription, login, logout, checkSubscription, updateProfile]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}
